import { UserInvitationOptionalResponseDto } from "@/lib/interfaces/invitation";
import { GetOnboardingResponseDto } from "@/lib/interfaces/onboarding";
import { UserInformationResponseDto } from "@/lib/interfaces/user";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2 } from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import useApiRequest from "../../lib/hooks/useRequest";
import { resolveProfileGate } from "../../lib/utils/profile-gate";
import { Form } from "../../ui/components/ui/form";
import { Context, ErrorEntity } from "../root/context";
import {
  fromOnboardingResponseDto,
  toOnboardingBrandingDto,
  toOnboardingOrganizationDto,
  toOnboardingSetupDto,
  toOnboardingUserDto,
} from "./onboarding-mappers";
import { OnboardingFormData, onboardingDefaultValues, onboardingSchema } from "./onboarding-schema";
import { DEFAULT_STEP, ONBOARDING_STEPS, STEP_VALIDATORS, getStepIndex, isValidStep } from "./onboarding-steps";
import OnboardingSidebar from "./OnboardingSidebar";
import BenefitsStep from "./steps/BenefitsStep";
import CompanyDataStep from "./steps/CompanyDataStep";
import EmailVerificationStep from "./steps/EmailVerificationStep";
import IdentityStep from "./steps/IdentityStep";
import LocationDataStep from "./steps/LocationDataStep";
import LocationsStep from "./steps/LocationsStep";
import MediaStep from "./steps/MediaStep";
import PaymentStep from "./steps/PaymentStep";
import PersonalDataStep from "./steps/PersonalDataStep";
import PositionDataStep from "./steps/PositionDataStep";
import WelcomeStep from "./steps/WelcomeStep";

const COMPLETABLE_STEP_IDS = ["1", "2", "3", "4", "5", "6", "7", "8"] as const;
const REQUIRED_VALIDATOR_STEP_IDS = ["1", "2", "3", "4", "6", "7"] as const;

const getCompletedStepsFromValues = (values: OnboardingFormData) => {
  const completed = new Set<string>();

  for (const stepId of REQUIRED_VALIDATOR_STEP_IDS) {
    const validator = STEP_VALIDATORS[stepId];
    if (!validator) continue;

    const result = validator.safeParse(values);
    if (result.success) {
      completed.add(stepId);
    }
  }

  if ((values.benefits ?? []).length > 0) {
    completed.add("5");
  }

  const hasBrandingMedia =
    (values.logoFiles ?? []).length > 0 ||
    (values.mediaFiles ?? []).length > 0 ||
    Boolean(values.ciColorPrimary?.trim());
  if (hasBrandingMedia) {
    completed.add("8");
  }

  return completed;
};

const NewOnboarding = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [skippedSteps, setSkippedSteps] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const { apiRequest } = useApiRequest();
  const { setError, setUser, user } = useContext(Context);

  const mail = useMemo(() => searchParams.get("mail"), [searchParams]);
  const shouldForceEmailVerification = useMemo(() => Boolean(mail) && completedSteps.has("2"), [completedSteps, mail]);
  const emailVerificationSearchParams = useMemo(
    () => ({ step: "email-verification", ...(mail ? { mail } : {}) }),
    [mail],
  );
  const hasCheckedEntryUserInfoRef = useRef(false);

  const form = useForm<OnboardingFormData>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: onboardingDefaultValues as OnboardingFormData,
    mode: "onTouched",
  });

  const currentStep = useMemo(() => {
    const param = searchParams.get("step");
    if (param && isValidStep(param)) return param;
    return DEFAULT_STEP;
  }, [searchParams]);

  const currentIndex = getStepIndex(currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === ONBOARDING_STEPS.length - 1;

  const canSkipCurrentStep = useMemo(() => ["3", "4", "5", "6", "7", "8"].includes(currentStep), [currentStep]);
  const isInvitedMember = useMemo(
    () => Boolean(user?.organizationId) && user?.permissionOrganizationAdmin === false,
    [user?.organizationId, user?.permissionOrganizationAdmin],
  );

  useEffect(() => {
    // One-time check on initial onboarding entry:
    // if backend says "email not verified", we inject `?mail=<error.mail>` so the email step can be handled by the flow.
    if (hasCheckedEntryUserInfoRef.current) return;

    hasCheckedEntryUserInfoRef.current = true;

    const checkUserEmailVerification = async () => {
      const userInfoRes = await apiRequest<UserInformationResponseDto>("users/userinfo", "GET");
      if (userInfoRes.data) return;

      const errObj = userInfoRes.error as any;
      const isNoSubscription =
        errObj?.entity === ErrorEntity.NoSubscriptionForbidden || errObj?.error === ErrorEntity.NoSubscriptionForbidden;
      const isEmailNotVerified =
        errObj?.entity === ErrorEntity.EMailNotVerifiedForbidden ||
        errObj?.error === ErrorEntity.EMailNotVerifiedForbidden;
      const errorMail = errObj?.mail;

      if (isNoSubscription) {
        setCompletedSteps((prev) => {
          const next = new Set(Array.from(prev));
          next.add("email-verification");
          return next;
        });
      }

      if (isEmailNotVerified && typeof errorMail === "string") {
        setSearchParams({ step: "email-verification", mail: errorMail }, { replace: true });
      }
    };

    void checkUserEmailVerification();
  }, [apiRequest, searchParams, setSearchParams]);

  useEffect(() => {
    if (hasHydrated) return;
    setHasHydrated(true);

    const hydrateFromOnboarding = async () => {
      const response = await apiRequest<GetOnboardingResponseDto>("onboarding", "GET");
      if (response.error || !response.data) return;

      const hydratedValues = {
        ...onboardingDefaultValues,
        ...fromOnboardingResponseDto(response.data),
      } as OnboardingFormData;

      form.reset(hydratedValues);
      const derivedCompletedSteps = getCompletedStepsFromValues(hydratedValues);
      setCompletedSteps((prev) => {
        const next = new Set(Array.from(prev));
        Array.from(derivedCompletedSteps).forEach((step) => next.add(step));
        return next;
      });

      const requestedStep = searchParams.get("step");
      const hasExplicitValidStep = Boolean(requestedStep && isValidStep(requestedStep));
      const shouldAutoNavigate = !hasExplicitValidStep || requestedStep === "willkommen";

      if (mail && derivedCompletedSteps.has("2")) {
        setSearchParams({ step: "email-verification", mail }, { replace: true });
        return;
      }

      if (shouldAutoNavigate) {
        const firstIncompleteStep = COMPLETABLE_STEP_IDS.find((stepId) => !derivedCompletedSteps.has(stepId));
        const targetStep = firstIncompleteStep ?? "9";
        setSearchParams({ step: targetStep, ...(mail ? { mail } : {}) }, { replace: true });
        return;
      }
    };

    void hydrateFromOnboarding();
  }, [apiRequest, form, hasHydrated, mail, searchParams, setSearchParams]);

  useEffect(() => {
    if (!shouldForceEmailVerification) return;
    if (currentStep === "email-verification") return;
    setSearchParams(emailVerificationSearchParams, { replace: true });
  }, [currentStep, emailVerificationSearchParams, setSearchParams, shouldForceEmailVerification]);

  const navigateTo = useCallback(
    (stepId: string) => {
      if (isInvitedMember && ["2", "3", "4", "5", "6", "7", "8", "9"].includes(stepId)) {
        navigate("/", { replace: true });
        return;
      }
      if (shouldForceEmailVerification) {
        setSearchParams(emailVerificationSearchParams, { replace: true });
        return;
      }
      setSearchParams({ step: stepId }, { replace: true });
    },
    [emailVerificationSearchParams, isInvitedMember, navigate, setSearchParams, shouldForceEmailVerification],
  );

  const handleStepClick = useCallback(
    (stepId: string) => {
      navigateTo(stepId);
    },
    [navigateTo],
  );

  const handleSkipModule = useCallback(() => {
    if (isInvitedMember) {
      navigate("/", { replace: true });
      return;
    }
    const module1 = ["3", "4", "5", "6"];
    const module2 = ["7"];
    const module3 = ["8"];

    const moduleSteps = module1.includes(currentStep) ? module1 : module2.includes(currentStep) ? module2 : module3;
    const nextStep = moduleSteps === module1 ? "7" : moduleSteps === module2 ? "8" : "9";

    setSkippedSteps((prev) => {
      const next = new Set(Array.from(prev));
      moduleSteps.forEach((step) => next.add(step));
      return next;
    });

    navigateTo(nextStep);
  }, [currentStep, isInvitedMember, navigate, navigateTo]);

  const handleFormSubmit = useCallback(() => {
    form.handleSubmit((data) => {
      console.log("Onboarding form submission:", data);
      setSubmitted(true);
    })();
  }, [form]);

  const checkAndTriggerInvitation = useCallback(async () => {
    // Best-effort: fetch user once (no retry), then check invitations.
    const userInfoRes = await apiRequest<UserInformationResponseDto>("users/userinfo", "GET");
    if (userInfoRes.data && setUser) {
      setUser(userInfoRes.data);
    }

    // Right after creating a user, the invitation state can lag briefly.
    // Retry the *invitation* endpoint (not userinfo) to avoid spamming userinfo.
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    let inviteRes: { data: UserInvitationOptionalResponseDto | undefined; error: unknown; status: number } | undefined =
      undefined;
    for (let attempt = 0; attempt < 5; attempt++) {
      const res = await apiRequest<UserInvitationOptionalResponseDto>("organizations/invitations", "GET");
      inviteRes = res;
      if (res.data) break;
      await sleep(300);
    }

    if (!inviteRes?.data) return;
    if (inviteRes.data?.invitation && setError) {
      setError({
        entity: ErrorEntity.CheckInvite,
        error: "CheckInvite",
        message: "CheckInvite",
        statusCode: 200,
      });
    }
  }, [apiRequest, setError, setUser]);

  const hasCheckedEntryInvitationRef = useRef(false);
  useEffect(() => {
    if (hasCheckedEntryInvitationRef.current) return;
    hasCheckedEntryInvitationRef.current = true;
    void checkAndTriggerInvitation();
  }, [checkAndTriggerInvitation]);

  const handleNext = useCallback(async () => {
    if (shouldForceEmailVerification && currentStep === "email-verification") {
      return;
    }

    if (currentStep === "9") {
      return;
    }

    if (isInvitedMember && ["2", "3", "4", "5", "6", "7", "8", "9"].includes(currentStep)) {
      const gate = await resolveProfileGate({ apiRequest, navigate, retry: { attempts: 5, delayMs: 300 } });
      if (gate.ok) {
        navigate("/", { replace: true });
      }
      return;
    }

    if (isLastStep) {
      handleFormSubmit();
      return;
    }

    const validator = STEP_VALIDATORS[currentStep];
    if (validator) {
      const result = validator.safeParse(form.getValues());
      if (!result.success) {
        for (const issue of result.error.issues) {
          form.setError(issue.path.join(".") as any, { message: issue.message });
        }
        return;
      }
    }

    const currentValues = form.getValues();
    if (currentStep === "1") {
      const response = await apiRequest("onboarding/user", "POST", {
        body: toOnboardingUserDto(currentValues),
        toast: { toastText: "Persönliche Daten gespeichert" },
      });
      if (response.error) return;

      // After creating the user, immediately re-check for pending invitations and open the invite modal if needed.
      await checkAndTriggerInvitation();
    }

    if (currentStep === "2") {
      const response = await apiRequest("onboarding/organization", "POST", {
        body: toOnboardingOrganizationDto(currentValues),
        toast: { toastText: "Unternehmensdaten gespeichert" },
      });
      if (response.error) return;
    }

    if (currentStep === "6") {
      const response = await apiRequest("onboarding/setup", "POST", {
        body: toOnboardingSetupDto(currentValues),
        toast: { toastText: "Standort- und Stellendaten gespeichert" },
      });
      if (response.error) return;
    }

    if (currentStep === "7") {
      const response = await apiRequest("onboarding/branding", "PATCH", {
        body: toOnboardingBrandingDto(currentValues),
        toast: { toastText: "Branding gespeichert" },
      });
      if (response.error) return;
    }

    setCompletedSteps((prev) => {
      const next = new Set(Array.from(prev));
      next.add(currentStep);
      return next;
    });

    if (currentStep === "2" && mail) {
      setSearchParams(emailVerificationSearchParams, { replace: true });
      return;
    }

    const nextStep = ONBOARDING_STEPS[currentIndex + 1];
    navigateTo(nextStep.id);
  }, [
    apiRequest,
    checkAndTriggerInvitation,
    currentIndex,
    currentStep,
    emailVerificationSearchParams,
    form,
    handleFormSubmit,
    isInvitedMember,
    isLastStep,
    mail,
    navigate,
    navigateTo,
    setSearchParams,
    shouldForceEmailVerification,
  ]);

  const handleBack = useCallback(() => {
    if (shouldForceEmailVerification) return;
    if (isFirstStep) return;
    const prevStep = ONBOARDING_STEPS[currentIndex - 1];
    navigateTo(prevStep.id);
  }, [currentIndex, isFirstStep, navigateTo, shouldForceEmailVerification]);

  const handleEmailVerificationContinue = useCallback(async () => {
    setCompletedSteps((prev) => {
      const next = new Set(Array.from(prev));
      next.add("email-verification");
      return next;
    });

    const gate = await resolveProfileGate({
      apiRequest,
      navigate,
      retry: { attempts: 5, delayMs: 300 },
      keepOnboardingForNoSubscription: true,
    });

    if (gate.ok && setUser) {
      setUser(gate.user);
    }

    const isInvitedMemberFromGate =
      gate.ok && Boolean(gate.user.organizationId) && gate.user.permissionOrganizationAdmin === false;
    if (isInvitedMember && !gate.ok && !gate.redirected) {
      window.location.replace("/");
      return;
    }

    if (gate.ok) {
      if (isInvitedMemberFromGate) {
        window.location.replace("/");
        return;
      }
      const firstIncompleteStep = COMPLETABLE_STEP_IDS.find((stepId) => !completedSteps.has(stepId));
      const targetStep = firstIncompleteStep ?? "9";
      setSearchParams({ step: targetStep }, { replace: true });
      return;
    }

    if (!gate.redirected && gate.entity === ErrorEntity.NoSubscriptionForbidden) {
      const firstIncompleteStep = COMPLETABLE_STEP_IDS.find((stepId) => !completedSteps.has(stepId));
      const targetStep = firstIncompleteStep ?? "9";
      setSearchParams({ step: targetStep }, { replace: true });
    }
  }, [apiRequest, completedSteps, isInvitedMember, navigate, setSearchParams, setUser]);

  const isWelcome = currentStep === "willkommen";
  const isPaymentStep = currentStep === "9";
  const isEmailVerificationStep = currentStep === "email-verification";

  return (
    <div className="flex min-h-screen bg-gray-50">
      <OnboardingSidebar
        currentStep={currentStep}
        completedSteps={completedSteps}
        skippedSteps={skippedSteps}
        onStepClick={handleStepClick}
      />

      <Form {...form}>
        <main className="ml-[270px] flex-1 flex flex-col items-center">
          <div className={`w-full px-8 pt-6 mx-auto ${isPaymentStep ? "max-w-none" : "max-w-3xl"} flex justify-end`}>
            {canSkipCurrentStep && (
              <button
                type="button"
                onClick={handleSkipModule}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-4"
              >
                Überspringen
              </button>
            )}
          </div>
          <div className={`flex-1 p-8 w-full mx-auto ${isPaymentStep ? "max-w-none" : "max-w-3xl"}`}>
            <StepContent
              stepId={currentStep}
              onNext={handleNext}
              form={form}
              submitted={submitted}
              onEmailVerificationContinue={handleEmailVerificationContinue}
            />
          </div>

          {!isWelcome && !submitted && !isPaymentStep && !isEmailVerificationStep && (
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-8 py-4 w-full">
              <div className="max-w-3xl mx-auto flex justify-between">
                {!isFirstStep ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Zurück
                  </button>
                ) : (
                  <div />
                )}
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 transition-colors"
                >
                  {isLastStep ? "Abschließen" : "Weiter"}
                </button>
              </div>
            </div>
          )}
        </main>
      </Form>
    </div>
  );
};

const SubmittedView = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div
      className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
      style={{ backgroundColor: "rgba(34, 197, 94, 0.1)" }}
    >
      <CheckCircle2 className="w-10 h-10 text-green-500" />
    </div>
    <h1 className="text-2xl font-bold text-gray-900 mb-4">Vielen Dank!</h1>
    <p className="text-gray-500 max-w-md">
      Wir verarbeiten nun alle Informationen und kommen in Kürze mit Ihrer finalisierten Recruiting-Zentrale und
      Kampagnen zum Übergabegespräch wieder auf Sie zu.
    </p>
  </div>
);

const StepContent = ({
  stepId,
  onNext,
  form,
  submitted,
  onEmailVerificationContinue,
}: {
  stepId: string;
  onNext: () => void;
  form: UseFormReturn<OnboardingFormData>;
  submitted: boolean;
  onEmailVerificationContinue: () => Promise<void>;
}) => {
  if (submitted) {
    return <SubmittedView />;
  }

  if (stepId === "willkommen") {
    return <WelcomeStep onNext={onNext} />;
  }

  if (stepId === "email-verification") {
    return <EmailVerificationStep onContinue={onEmailVerificationContinue} />;
  }

  if (stepId === "1") {
    return <PersonalDataStep form={form} />;
  }

  if (stepId === "2") {
    return <CompanyDataStep form={form} />;
  }

  if (stepId === "3") {
    return <LocationsStep form={form} />;
  }

  if (stepId === "4") {
    return <LocationDataStep form={form} />;
  }

  if (stepId === "5") {
    return <BenefitsStep form={form} />;
  }

  if (stepId === "6") {
    return <PositionDataStep form={form} />;
  }

  if (stepId === "7") {
    return <IdentityStep form={form} />;
  }

  if (stepId === "8") {
    return <MediaStep form={form} />;
  }

  if (stepId === "9") {
    return <PaymentStep />;
  }

  return null;
};

export default NewOnboarding;
